import React, { createContext, useContext, useState, useCallback } from "react";

const NotificationContext = createContext(null);

let idCounter = 1;

// TalentTalks palette
const PALETTE = {
  primary: "#4B1498",
  violet: "#A66FFF",
  ink: "#191F2D",
  surface: "#F0EBFF",
};

// Per-type look (colors only)
const STYLES = {
  success: {
    background: PALETTE.surface,
    border: `1px solid ${PALETTE.violet}`,
    text: PALETTE.ink,
    icon: PALETTE.primary,
    close: PALETTE.primary,
  },
  error: {
    background: PALETTE.ink,
    border: `1px solid ${PALETTE.ink}`,
    text: "#FFFFFF",
    icon: PALETTE.violet,
    close: PALETTE.violet,
  },
  info: {
    background: "#FFFFFF",
    border: `1px solid ${PALETTE.surface}`,
    text: PALETTE.ink,
    icon: PALETTE.violet,
    close: PALETTE.violet,
  },
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const notify = useCallback((type, message, options = {}) => {
    const id = idCounter++;
    const ttl = options.ttl || 4000;
    console.log("🔔 Notification added:", { id, type, message });
    setNotifications((prev) => {
      const updated = [{ id, type, message }, ...prev];
      console.log("📢 Current notifications:", updated);
      return updated;
    });
    if (ttl > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, ttl);
    }
    return id;
  }, []);

  const remove = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, remove }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col items-end gap-3" style={{pointerEvents: 'none'}}>
        {console.log("🎨 Rendering notifications, count:", notifications.length)}
        {notifications.map((n) => {
          const s = STYLES[n.type] || STYLES.info;
          return (
            <div
              key={n.id}
              className="pointer-events-auto max-w-sm w-full rounded-lg shadow-lg px-4 py-3 transition transform origin-top-right flex items-start gap-3"
              style={{
                pointerEvents: 'auto',
                background: s.background,
                border: s.border,
                boxShadow: "0 12px 28px -12px rgba(75,20,152,.35)",
              }}
            >
              <div className="flex-shrink-0 mt-0.5">
                {n.type === "success" ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke={s.icon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : n.type === "error" ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" stroke={s.icon} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 16h-1v-4h-1m1-4h.01" stroke={s.icon} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>

              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: s.text }}>{n.message}</div>
              </div>

              <div className="ml-2">
                <button
                  onClick={() => remove(n.id)}
                  style={{ color: s.close }}
                  className="opacity-80 hover:opacity-100"
                  aria-label="dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};